"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import AnimatedSection, { AnimatedElement } from "../AnimatedSection";
import {
  Crown,
  DollarSign,
  AlertTriangle,
  Eye,
  Lock,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Editorial showcase of the lead Nightlife feature — the smart VIP Table Map.
 * Two-column layout:
 *   Left: live floor-plan mockup (CSS only) with color-coded table states
 *   Right: 6 capability callouts that read like a magazine sidebar
 *
 * The mockup is the "hero" of the section — this is the feature that
 * differentiates Redy from Toast / Square for venues.
 */
export default function NightlifeVipShowcase() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      {/* Atmosphere */}
      <div className="absolute top-1/4 -left-32 w-[700px] h-[700px] bg-[#7C3AED]/22 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-[600px] h-[600px] bg-[#F472B6]/15 rounded-full blur-[140px] pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        {/* Eyebrow + asymmetric headline */}
        <AnimatedSection className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <div className="text-[11px] font-bold tracking-widest uppercase text-[#F472B6] mb-4">
                {t("The lead feature", "El feature líder")}
              </div>
              <h2 className="text-5xl md:text-[76px] font-black tracking-tight leading-[0.95]">
                {t("The brain of", "El cerebro de")}
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, #C4B5FD 0%, #F472B6 50%, #FB923C 100%)",
                  }}
                >
                  {t("the night.", "la noche.")}
                </span>
              </h2>
            </div>
            <div className="md:col-span-4">
              <p className="text-[15px] font-medium text-white/55 leading-relaxed">
                {t(
                  "VIP Table Map is the smart layer your venue has been missing. Every host, supervisor and bartender sees the floor in real time — one tap from action.",
                  "VIP Table Map es la capa inteligente que le faltaba a tu venue. Cada host, supervisor y bartender ve el floor en vivo — a un tap de acción."
                )}
              </p>
            </div>
          </div>
        </AnimatedSection>

        {/* Two-column showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left: Floor plan mockup */}
          <AnimatedElement
            delay={0.1}
            className="lg:col-span-7 relative bg-white/[0.04] border border-white/10 rounded-[28px] p-6 md:p-10 overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-[#7C3AED]/20 rounded-full blur-[100px] pointer-events-none" />

            {/* Mock header bar */}
            <div className="relative flex items-center justify-between mb-6 px-3">
              <div>
                <div className="text-[9px] font-bold tracking-widest uppercase text-white/40 mb-1">
                  {t("Friday Night · 11:32 PM", "Viernes Noche · 11:32 PM")}
                </div>
                <div className="text-[14px] font-black text-white">
                  {t("VIP Floor", "Floor VIP")} —{" "}
                  <span className="text-[#C4B5FD]">8 / 12 {t("seated", "ocupadas")}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-redy-green/15 border border-redy-green/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-redy-green animate-pulse" />
                  <span className="text-[9px] font-black tracking-widest uppercase text-redy-green">
                    {t("Live", "En vivo")}
                  </span>
                </div>
              </div>
            </div>

            {/* Floor plan grid */}
            <FloorPlan t={t} />

            {/* Legend */}
            <div className="relative mt-6 flex flex-wrap gap-x-5 gap-y-2 px-3 text-[10px] font-bold uppercase tracking-widest">
              {[
                { color: "bg-redy-green", label: t("Open", "Abierta") },
                { color: "bg-[#FB923C]", label: t("Reserved", "Reservada") },
                { color: "bg-[#7C3AED]", label: t("Occupied", "Ocupada") },
                { color: "bg-redy-red", label: t("Below minimum", "Bajo mínimo") },
              ].map((l) => (
                <div key={l.label} className="flex items-center gap-1.5 text-white/55">
                  <span className={`w-2 h-2 rounded-full ${l.color}`} />
                  {l.label}
                </div>
              ))}
            </div>
          </AnimatedElement>

          {/* Right: Capability callouts */}
          <div className="lg:col-span-5 space-y-3">
            {CALLOUTS.map((c, i) => (
              <CalloutCard key={c.titleEn} callout={c} delay={0.15 + 0.06 * i} t={t} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Floor plan mockup
// ─────────────────────────────────────────────────────────────────────────────

type Translator = (en: string, es: string) => string;

interface Table {
  id: string;
  state: "open" | "reserved" | "occupied" | "low";
  min: string;
  // Position in 12-column grid (col-span × row-span)
  cls: string;
  guests?: number;
  spent?: string;
  host?: string;
}

const TABLES: Table[] = [
  { id: "VIP-1", state: "occupied", min: "$2K", cls: "col-span-3 row-span-2", guests: 6, spent: "$2,840", host: "Marcus" },
  { id: "VIP-2", state: "occupied", min: "$1.5K", cls: "col-span-3 row-span-1", guests: 4, spent: "$1,920", host: "Lana" },
  { id: "VIP-3", state: "reserved", min: "$3K", cls: "col-span-3 row-span-1", host: "Marcus" },
  { id: "VIP-4", state: "low", min: "$1K", cls: "col-span-3 row-span-1", guests: 3, spent: "$420", host: "Tomás" },
  { id: "B-1", state: "occupied", min: "$800", cls: "col-span-3 row-span-1", guests: 4, spent: "$1,150", host: "Lana" },
  { id: "B-2", state: "open", min: "$800", cls: "col-span-3 row-span-1" },
  { id: "B-3", state: "open", min: "$800", cls: "col-span-3 row-span-1" },
  { id: "L-1", state: "occupied", min: "$500", cls: "col-span-6 row-span-1", guests: 8, spent: "$960", host: "Lana" },
  { id: "L-2", state: "reserved", min: "$500", cls: "col-span-3 row-span-1", host: "Marcus" },
  { id: "L-3", state: "open", min: "$500", cls: "col-span-3 row-span-1" },
];

const STATE_STYLES: Record<Table["state"], { bg: string; border: string; text: string }> = {
  open: { bg: "bg-redy-green/12", border: "border-redy-green/30", text: "text-redy-green" },
  reserved: { bg: "bg-[#FB923C]/12", border: "border-[#FB923C]/30", text: "text-[#FB923C]" },
  occupied: { bg: "bg-[#7C3AED]/15", border: "border-[#7C3AED]/35", text: "text-[#C4B5FD]" },
  low: { bg: "bg-redy-red/15", border: "border-redy-red/35", text: "text-redy-red" },
};

function FloorPlan({ t }: { t: Translator }) {
  return (
    <div className="relative grid grid-cols-12 gap-2 md:gap-3">
      {TABLES.map((table, i) => {
        const style = STATE_STYLES[table.state];
        return (
          <motion.div
            key={table.id}
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ delay: 0.03 * i, duration: 0.35 }}
            className={`${table.cls} ${style.bg} border ${style.border} rounded-[12px] p-2.5 flex flex-col justify-between min-h-[68px] md:min-h-[80px]`}
          >
            <div className="flex items-center justify-between">
              <div className={`text-[10px] md:text-[11px] font-black ${style.text}`}>
                {table.id}
              </div>
              {table.state === "occupied" && (
                <span className="w-1.5 h-1.5 rounded-full bg-redy-green animate-pulse" />
              )}
              {table.state === "low" && (
                <AlertTriangle className="w-3 h-3 text-redy-red" />
              )}
            </div>

            {table.guests ? (
              <div className="flex items-center justify-between text-[9px] font-bold text-white/55 mt-2">
                <span>
                  {table.guests} {t("guests", "personas")}
                </span>
                <span className="text-white/75 font-black">{table.spent}</span>
              </div>
            ) : (
              <div className="text-[9px] font-bold uppercase tracking-widest text-white/30 mt-2">
                {table.state === "open"
                  ? `${t("Min", "Mín")} ${table.min}`
                  : `${t("Held for", "Reservada")} ${table.host}`}
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Capability callouts
// ─────────────────────────────────────────────────────────────────────────────

interface Callout {
  icon: LucideIcon;
  accent: string;
  titleEn: string;
  titleEs: string;
  bodyEn: string;
  bodyEs: string;
}

const CALLOUTS: Callout[] = [
  {
    icon: Crown,
    accent: "#C4B5FD",
    titleEn: "Bottle service in two taps",
    titleEs: "Bottle service en dos taps",
    bodyEn: "Preset packages with sparklers, mixers and add-ons. Fire to bar instantly.",
    bodyEs: "Paquetes preset con sparklers, mixers y extras. Dispara a barra al instante.",
  },
  {
    icon: DollarSign,
    accent: "#10B981",
    titleEn: "Deposits on record",
    titleEs: "Depósitos registrados",
    bodyEn: "Record the deposit you collected when the table is booked. It stays on the table for the host and supervisor.",
    bodyEs: "Registras el depósito que cobraste al reservar la mesa. Queda en la mesa para el host y el supervisor.",
  },
  {
    icon: AlertTriangle,
    accent: "#FB923C",
    titleEn: "Minimum tracking — live",
    titleEs: "Tracking de mínimos en vivo",
    bodyEn: "Every table shows its minimum and what's been spent. Below the minimum, closing it takes a supervisor PIN.",
    bodyEs: "Cada mesa muestra su mínimo y lo consumido. Bajo el mínimo, cerrarla requiere PIN de supervisor.",
  },
  {
    icon: Users,
    accent: "#F472B6",
    titleEn: "Guest CRM follows them",
    titleEs: "CRM del cliente los sigue",
    bodyEn: "Last visit and lifetime spend on file in your customer list.",
    bodyEs: "Última visita y gasto total registrados en tu lista de clientes.",
  },
  {
    icon: Lock,
    accent: "#F472B6",
    titleEn: "Supervisor PIN gate",
    titleEs: "PIN de supervisor",
    bodyEn: "Comps, voids, transfers and closes require a manager — every move logged.",
    bodyEs: "Comps, voids, transfers y cierres requieren manager — cada acción registrada.",
  },
  {
    icon: Eye,
    accent: "#C4B5FD",
    titleEn: "Supervisor sees everything",
    titleEs: "Supervisor lo ve todo",
    bodyEn: "Live floor + revenue dashboard on one tablet. Spot bottlenecks before they bottleneck.",
    bodyEs: "Floor en vivo + dashboard de revenue en un tablet. Detecta cuellos de botella antes.",
  },
];

function CalloutCard({ callout, delay, t }: { callout: Callout; delay: number; t: Translator }) {
  const Icon = callout.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay, duration: 0.5 }}
      className="flex items-start gap-4 bg-white/[0.04] border border-white/10 rounded-[20px] p-5 hover:bg-white/[0.06] hover:border-white/15 transition-colors"
    >
      <div
        className="w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${callout.accent}22` }}
      >
        <Icon className="w-4 h-4" style={{ color: callout.accent }} />
      </div>
      <div>
        <div className="text-[15px] font-black text-white mb-1 leading-snug">
          {t(callout.titleEn, callout.titleEs)}
        </div>
        <p className="text-[13px] font-medium text-white/55 leading-relaxed">
          {t(callout.bodyEn, callout.bodyEs)}
        </p>
      </div>
    </motion.div>
  );
}
